/**
 * Dictation cleanup.
 *
 * Deepgram's nova-3-medical model with `dictation: true` already turns spoken
 * "full stop" and "new paragraph" into punctuation, and `numerals: true`
 * handles most spoken numbers. What it does not do is know Beam's house style,
 * or that the speaker is Australian and the training data is not.
 *
 * This pass runs before the main style pass and only handles things that are
 * specific to speech: verbal commands the recogniser missed, filler, false
 * starts, the spoken forms of punctuation that survive as words, and the
 * escape sequences the transcription source emits as literal text.
 */
/**
 * Line breaks that arrived as literal text rather than as characters.
 *
 * Observed against Deepgram with `dictation: true`: a spoken "new line" comes
 * back not as a control character but as the two characters backslash and n.
 * JSON transport carries those two characters faithfully all the way to the
 * box, so the doctor sees \n in the middle of their sentence. The words "new
 * line" are already handled below; the escape sequence was not, which is why a
 * doctor saying the same thing got a different result depending on whether the
 * recogniser chose to interpret it.
 *
 * Whether the escaping happens in the model, the API or a client library has
 * not been established: confirming that needs a live Deepgram connection this
 * work did not have. It does not matter for the fix. A backslash followed by n
 * in dictated medical prose is a transport artefact under every reading, and
 * the alternative reading, that a doctor dictated a backslash, is not one.
 *
 * A run of breaks collapses to a paragraph break rather than stacking blank
 * lines: nobody dictates three blank lines, and the report body has no use for
 * them. One break stays one break.
 *
 * Written as a single rule over the whole run rather than one rule per escape
 * so that mixed input, a real newline beside a literal one, is counted once.
 * A run that is already correct produces an edit that changes nothing, and the
 * engine drops those before they reach the change list.
 */
const BREAK_TOKEN = String.raw `(?:\\r\\n|\\n|\r\n|\n)`;
const HORIZONTAL = String.raw `(?:[ \t]|\\t)*`;
const BREAK_RUN = new RegExp(`${HORIZONTAL}${BREAK_TOKEN}(?:${HORIZONTAL}${BREAK_TOKEN})*${HORIZONTAL}`, 'g');
const BREAK_COUNT = new RegExp(BREAK_TOKEN, 'g');
/** A literal tab escape that is not part of a line break run. */
const LITERAL_TAB = /\\t/g;
/**
 * Lists, spoken.
 *
 * The marker goes into the text as the character the doctor is going to see,
 * not as a code for it. A doctor dictating a list of medicines wants a list of
 * medicines in the box, and a box holding `- item` is a box they have to read
 * past. The bullet is therefore U+2022 and the ordered marker is the digit and
 * a full stop, which is what both the box and the printed report show.
 *
 * The renderer promotes runs of these lines back into real lists so the
 * printed report wraps with a hanging indent, but nothing downstream has to:
 * the text on its own is already correct, which is the property that matters
 * when a doctor pastes it into their own template.
 */
/**
 * A bullet already starts its own line, so a doctor who says "new line bullet"
 * is asking for one line break and not two. The redundant break is absorbed
 * here rather than left to collide with the rule that would have made it: two
 * rules matching the same words is how one of them silently loses its span and
 * the words "new line" end up in the report.
 *
 * Only horizontal whitespace is taken from in front. Eating a real line break
 * would join this item to the paragraph above it.
 */
const REDUNDANT_BREAK = String.raw `(?:\b(?:new line|next line|new paragraph|next paragraph)\b[ \t]*)?`;
const BULLET_COMMAND = new RegExp(String.raw `[ \t]*${REDUNDANT_BREAK}\b(?:next bullet|bullet point)\b[\s.,]*`, 'gi');
/**
 * A bare "bullet" is not always the command.
 *
 * This is a medicolegal report and "bullet wound" is a thing a doctor writes.
 * The projectile senses are excluded outright, and what is left is raised as
 * `likely` rather than `certain`, so a doctor who did mean the noun sees it in
 * the "worth checking" count with an undo beside it. "Bullet point" and "next
 * bullet" are unambiguous commands and stay certain.
 */
const BULLET_BARE = new RegExp(String.raw `[ \t]*${REDUNDANT_BREAK}\bbullet\b` +
    String.raw `(?!\s*(?:wound|fragment|hole|entry|exit|injur|calibre|caliber|proof))[\s.,]*`, 'gi');
/**
 * `number one` through `number twenty`, but only where an item can start.
 *
 * The guard is the whole design. "The number one cause of low back pain" is
 * ordinary prose and turning it into a list item mid-sentence would be worse
 * than not having the feature, so the phrase only counts at the start of the
 * dictation or after a line break or a full stop. What survives the guard is
 * still raised as `likely` rather than `certain`, so it lands in the "worth
 * checking" count with an undo beside it.
 */
const ORDINAL_WORDS = [
    'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
    'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen',
    'eighteen', 'nineteen', 'twenty',
];
const NUMBERED_ITEM = new RegExp(String.raw `(^|\n|\.|\bfull stop\b|\bperiod\b|\bnew line\b|\bnew paragraph\b)` +
    String.raw `\s*\bnumber\s+(${ORDINAL_WORDS.join('|')})\b[\s.,]*`, 'gi');
/**
 * The boundary, rewritten as the characters it stands for.
 *
 * Every rule in this pass matches the original transcript, so by the time this
 * one runs the doctor's spoken "full stop" is still the words "full stop": the
 * rule that turns it into a dot has not been applied yet and cannot be seen.
 * That means the boundary has to accept the spoken forms as well as the
 * characters, and having claimed the span, reproduce what was there.
 */
function boundaryText(lead) {
    const word = lead.trim().toLowerCase();
    if (word === 'new paragraph')
        return '\n';
    if (word === '' || word === 'new line' || lead === '\n')
        return '';
    return '.';
}
/** Spoken punctuation that reached the transcript as words. */
const SPOKEN_PUNCTUATION = [
    [/\s*\b(?:full stop|period)\b\s*/gi, '. ', 'spoken-full-stop'],
    [/\s*\bcomma\b\s*/gi, ', ', 'spoken-comma'],
    [/\s*\b(?:semi colon|semicolon)\b\s*/gi, '; ', 'spoken-semicolon'],
    [/\s*\bcolon\b(?!\s*(?:cancer|carcinoma|polyp|oscopy))\s*/gi, ': ', 'spoken-colon'],
    [/\s*\bquestion mark\b\s*/gi, '? ', 'spoken-question-mark'],
    [/\s*\b(?:new paragraph|next paragraph)\b\s*/gi, '\n\n', 'spoken-paragraph'],
    [/\s*\b(?:new line|next line)\b\s*/gi, '\n', 'spoken-newline'],
    [/\s*\bopen (?:bracket|paren|parenthesis)\b\s*/gi, ' (', 'spoken-open-bracket'],
    [/\s*\bclose[d]? (?:bracket|paren|parenthesis)\b\s*/gi, ') ', 'spoken-close-bracket'],
    [/\s*\b(?:open quote|quote unquote)\b\s*/gi, ' "', 'spoken-quote'],
    [/\s*\bhyphen\b\s*/gi, '-', 'spoken-hyphen'],
    [/\s*\bslash\b\s*/gi, '/', 'spoken-slash'],
];
export const SPOKEN_COMMANDS = [
    {
        say: ['full stop', 'period'],
        produces: 'a full stop',
        example: { said: 'no wasting full stop', becomes: 'no wasting.' },
    },
    {
        say: ['comma'],
        produces: 'a comma',
        example: { said: 'on bending comma and on sitting', becomes: 'on bending, and on sitting' },
    },
    {
        say: ['semicolon', 'semi colon'],
        produces: 'a semicolon',
        example: { said: 'flexion 50 semicolon extension 10', becomes: 'flexion 50; extension 10' },
    },
    {
        say: ['colon'],
        produces: 'a colon',
        example: { said: 'the findings colon a disc protrusion', becomes: 'the findings: a disc protrusion' },
    },
    {
        say: ['question mark'],
        produces: 'a question mark',
        example: { said: 'is she fit for work question mark', becomes: 'is she fit for work?' },
    },
    {
        say: ['new line', 'next line'],
        produces: 'a line break',
        example: { said: 'first line new line second line', becomes: 'first line\nsecond line' },
    },
    {
        say: ['new paragraph', 'next paragraph'],
        produces: 'a blank line and a new paragraph',
        example: { said: 'first para new paragraph second para', becomes: 'first para\n\nsecond para' },
    },
    {
        say: ['bullet point', 'next bullet', 'bullet'],
        produces: 'a new line starting with a bullet. Say "new line" to finish the '
            + 'list: a full stop stays inside the item, because an item can have one',
        example: {
            said: 'medicines bullet point tapentadol bullet point ibuprofen',
            becomes: 'medicines\n• tapentadol\n• ibuprofen',
        },
    },
    {
        say: ['number one', 'number two', 'and so on to number twenty'],
        produces: 'a new line starting with that number, for an ordered list. '
            + 'Pause first: this one only counts at the start of a sentence',
        example: {
            said: 'the plan is as follows full stop number one physiotherapy full stop number two review',
            becomes: 'the plan is as follows.\n1. physiotherapy.\n2. review',
        },
    },
    {
        say: ['open bracket', 'close bracket'],
        produces: 'a round bracket',
        example: { said: 'the disc open bracket L4 slash 5 close bracket', becomes: 'the disc (L4/5)' },
    },
    {
        say: ['slash'],
        produces: 'a forward slash',
        example: { said: 'L4 slash 5', becomes: 'L4/5' },
    },
    {
        say: ['hyphen'],
        produces: 'a hyphen',
        example: { said: 'three hyphen six months', becomes: 'three-six months' },
    },
];
/**
 * Filler. Removed only when it stands alone between sentence boundaries or
 * commas, so a clinically meaningful "well" in "the wound healed well" survives.
 */
const FILLER = /(?<=^|[.!?,;]\s|\n)\s*(?:um+|uh+|er+|ah+|hmm+|you know|i mean|sort of|kind of|like)\s*,?\s*/gi;
/** An immediate word repetition: "the the", "patient patient". */
const STUTTER = /\b(\w+)(\s+\1\b)+/gi;
/** A spoken correction: "no sorry", "scratch that", "strike that". */
const SELF_CORRECTION = /,?\s*\b(?:no,? sorry|sorry,? no|scratch that|strike that|delete that|correction)\b[,.]?\s*/gi;
/**
 * Doctors say "left" and "right" constantly and the recogniser sometimes
 * returns "write" for "right". Only corrected next to an anatomical word,
 * because "write" is a real word a report may need.
 */
const ANATOMY = String.raw `(?:shoulder|elbow|wrist|hand|thumb|finger|hip|knee|ankle|foot|toe|arm|leg|side|eye|ear|kidney|lung|clavicle|scapula|humerus|radius|ulna|femur|tibia|fibula|patella|calcaneus|rotator cuff|acl|mcl|pcl|meniscus)`;
const WRITE_FOR_RIGHT = new RegExp(String.raw `\b(write|rite)\s+(?=${ANATOMY}\b)`, 'gi');
const ANATOMY_WRITE = new RegExp(String.raw `\b(?<=${ANATOMY}\s)(write|rite)\b`, 'gi');
/** "point five" for a decimal the recogniser split. */
const SPLIT_DECIMAL = /\b(\d+)\s+point\s+(\d+)\b/gi;
/** Words a speaker genuinely says twice in a row. */
const LEGITIMATE_DOUBLES = new Set(['had', 'that', 'is', 'is', 'no', 'very', 'so']);
/**
 * Number words. A repetition of one of these is almost always a spoken year or
 * a spoken figure, not a stutter.
 */
const NUMBER_WORDS = new Set([
    'zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight',
    'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen',
    'sixteen', 'seventeen', 'eighteen', 'nineteen', 'twenty', 'thirty', 'forty',
    'fifty', 'sixty', 'seventy', 'eighty', 'ninety', 'hundred', 'thousand',
]);
export function findDictationFixes(text) {
    const hits = [];
    const claimed = [];
    const free = (s, e) => !claimed.some(([cs, ce]) => s < ce && cs < e);
    const push = (s, e, replacement, rule, confidence = 'certain', note) => {
        if (!free(s, e))
            return;
        claimed.push([s, e]);
        hits.push({ start: s, end: e, replacement, rule, confidence, ...(note ? { note } : {}) });
    };
    // Transport artefacts first. They claim their span before any speech rule
    // looks at it, because a literal escape is not speech and the speech rules
    // have no business matching inside one.
    for (const m of text.matchAll(BREAK_RUN)) {
        const run = m[0];
        const breaks = run.match(BREAK_COUNT)?.length ?? 0;
        if (breaks === 0)
            continue;
        push(m.index, m.index + run.length, breaks === 1 ? '\n' : '\n\n', breaks === 1 ? 'literal-line-break' : 'literal-paragraph-break', 'certain', 'line break from the transcription source, written as an escape sequence');
    }
    for (const m of text.matchAll(LITERAL_TAB)) {
        push(m.index, m.index + m[0].length, '\t', 'literal-tab', 'certain', 'tab from the transcription source, written as an escape sequence');
    }
    for (const m of text.matchAll(SELF_CORRECTION)) {
        push(m.index, m.index + m[0].length, ' ', 'self-correction', 'likely', 'spoken correction removed. Check the sentence still says what was meant.');
    }
    // Lists before the punctuation rules: "bullet point" ends in a word the
    // decimal rule also looks at, and an item marker is not punctuation.
    // The unambiguous forms claim their span first, so the bare rule below
    // cannot match the word "bullet" inside "bullet point".
    for (const m of text.matchAll(BULLET_COMMAND)) {
        push(m.index, m.index + m[0].length, '\n• ', 'spoken-bullet', 'certain', 'spoken bullet, written as a list item');
    }
    for (const m of text.matchAll(BULLET_BARE)) {
        push(m.index, m.index + m[0].length, '\n• ', 'spoken-bullet', 'likely', 'heard as a spoken bullet rather than as the noun');
    }
    for (const m of text.matchAll(NUMBERED_ITEM)) {
        const position = ORDINAL_WORDS.indexOf(m[2].toLowerCase()) + 1;
        if (position === 0)
            continue;
        // The boundary is kept: it is what made this an item and not prose, and
        // swallowing it runs two sentences together.
        const keep = boundaryText(m[1] ?? '');
        push(m.index, m.index + m[0].length, `${keep}\n${position}. `, 'spoken-numbered-item', 'likely', 'heard as the start of a numbered item rather than as prose');
    }
    for (const [pattern, replacement, rule] of SPOKEN_PUNCTUATION) {
        for (const m of text.matchAll(pattern)) {
            const s = m.index;
            const e = s + m[0].length;
            if (free(s, e)) {
                push(s, e, replacement, rule);
                continue;
            }
            // Two commands in a row ("full stop new paragraph", the commonest pairing
            // in dictation) share the space between them, and the first has claimed
            // it. The second claims its words alone rather than being dropped, which
            // is what silently ran two paragraphs together.
            const lead = m[0].length - m[0].trimStart().length;
            const trail = m[0].length - m[0].trimEnd().length;
            const from = s + lead;
            const to = free(from, e) ? e : e - trail;
            // Only a leading space is dropped from the replacement: a break is whitespace too, and it is the point.
            push(from, to, replacement.replace(/^ +/, ''), rule);
        }
    }
    for (const m of text.matchAll(SPLIT_DECIMAL)) {
        push(m.index, m.index + m[0].length, `${m[1]}.${m[2]}`, 'split-decimal');
    }
    for (const m of text.matchAll(WRITE_FOR_RIGHT)) {
        push(m.index, m.index + m[1].length, 'right', 'write-for-right', 'likely', 'heard next to an anatomical term, so read as the side of the body');
    }
    for (const m of text.matchAll(ANATOMY_WRITE)) {
        push(m.index, m.index + m[0].length, 'right', 'write-for-right', 'likely', 'heard next to an anatomical term, so read as the side of the body');
    }
    for (const m of text.matchAll(STUTTER)) {
        const word = m[1].toLowerCase();
        // Legitimate doubles exist: "had had", "that that".
        if (LEGITIMATE_DOUBLES.has(word))
            continue;
        // A spoken year repeats a number word: "twenty twenty four" is 2024, not a
        // stutter. Removing the second "twenty" turns the date into 24, which is
        // the kind of error that puts a date of injury four years out.
        if (NUMBER_WORDS.has(word))
            continue;
        push(m.index, m.index + m[0].length, m[1], 'repeated-word', 'likely', 'immediate repetition removed');
    }
    for (const m of text.matchAll(FILLER)) {
        const replacement = m[0].startsWith('\n') ? '\n' : m[0].match(/^\s/) ? ' ' : '';
        push(m.index, m.index + m[0].length, replacement, 'filler', 'likely', 'spoken filler removed');
    }
    return hits.sort((a, b) => a.start - b.start);
}
/**
 * Keyterm assembly for Deepgram nova-3.
 *
 * Nova-3 caps keyterms at 100. The layers are ordered by how much each is worth
 * per slot: case terms first, because a claimant's surname or their employer's
 * name is the word most likely to be wrong and the one a reader notices; then
 * the doctor's own vocabulary; then their specialty; then the global list.
 *
 * Case and doctor terms are patient-identifying or commercially specific and
 * must never be committed to a repository. They are passed in at request time.
 */
export const MAX_KEYTERMS = 100;
export function assembleKeyterms(layers) {
    const ordered = [
        ...(layers.caseTerms ?? []),
        ...(layers.doctorTerms ?? []),
        ...(layers.specialtyTerms ?? []),
        ...layers.globalTerms,
    ];
    const seen = new Set();
    const out = [];
    for (const raw of ordered) {
        const term = raw.trim();
        if (!term)
            continue;
        const key = term.toLowerCase();
        if (seen.has(key))
            continue;
        seen.add(key);
        out.push(term);
        if (out.length === MAX_KEYTERMS)
            break;
    }
    return out;
}
//# sourceMappingURL=dictation.js.map